import React, { useState, useEffect, useMemo, useRef, createContext } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useLocation, Navigate, Outlet, useOutlet, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { useQuery } from '@tanstack/react-query'
import cx from 'classnames';

import HomePage from "./home-page";
import Dashboard from "./dashboard";
import Transactions from "./transactions";
import Categories from "./categories";
import Rules from "./rules";
import LLMChat from "./llm-chat";
import { fetchProviders, fetchProviderAuthUrl, fetchConnections, fetchConnectionReauthUrl, fetchAccounts, fetchSettings, fetchCategories, fetchSpaces } from "./api";
import { applyCode, useAuth, SignupError, LoginFailedError } from "./auth";
import { useSpaces, createSpaceScopedFetch } from "./spaces";
import { AppProvider, useAppContext } from "./app-context";

import styles from './app.module.css';
import ModalLayout, { smallModal, mediumModal } from './modal-layout';
import { formatMoney } from './utils';

import tinyLogo from '../logos/tiny1.png';

// Dynamic import of all PNG files from the logos/resized directory
const logoContext = require.context('../logos/resized/', false, /\.png$/);
const logos = logoContext.keys().map(logoContext);

const ProviderSelectorModal = ({ show, providers, onClose, spaceScopedFetch }) => {
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const filteredProviders = providers.filter(
        provider => provider.displayName.toLowerCase().includes(search.toLowerCase())
    );

    const [loadingProviderAuthUrl, setLoadingProviderAuthUrl] = useState(false);

    const connect = async provider => {
        if(loadingProviderAuthUrl) {
            return;
        }
        setLoadingProviderAuthUrl(true);
        try {
            setLoading(true);
            const authUrl = await fetchProviderAuthUrl(provider, spaceScopedFetch);
            window.location.href = authUrl;
        } finally {
            setLoadingProviderAuthUrl(false);
            setTimeout(() => {
                setLoading(false);
            }, 1000);
        }
    };
    return (
        <Modal
            isOpen={show}
            onRequestClose={() => onClose(false)}
            style={smallModal}
        >
            <ModalLayout
                title="Add a bank account"
                onCancel={() => onClose(false)}
                className={styles.providerSelectorModal}
            >
                <input
                    type="text"
                    placeholder="Search banks..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    autoFocus={true}
                />
                <ul>
                    {filteredProviders.map(provider =>
                        <li
                            key={provider.uri}
                        >
                            <button
                                onClick={() => connect(provider)}
                                className={cx(styles.provider, {
                                    [styles.disabled]: loading
                                })}
                            >
                                <img src={provider.logoUrl} alt={`${provider.displayName} logo`} />
                                <span>{provider.displayName}</span>
                            </button>
                        </li>
                    )}
                </ul>
            </ModalLayout>
        </Modal>
    );
};

const LoginModal = ({ show, onLogin }) => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [loginFailedError, setLoginFailedError] = useState(false);
    const [error, setError] = useState(false);
    const navigate = useNavigate();
    const [logo, setLogo] = useState();

    useEffect(() => {
        const randomLogo = logos[Math.floor(Math.random() * logos.length)];
        setLogo(randomLogo);
    }, []);

    const attemptLogin = async () => {
        try {
            setLoginFailedError(false);
            setError(false);
            await onLogin(username, password);
            // Navigate to app on successful login
            navigate('/app/at-a-glance');
        } catch (e) {
            console.error('Login error:', e);
            if (e instanceof LoginFailedError) {
                setLoginFailedError(true);
            } else {
                setError(true);
                // Log additional details for debugging but don't expose to user
                console.error('Detailed login error:', e.message || 'Unknown error');
            }
        }
    };

    return (
        <Modal
            isOpen={show}
            style={mediumModal}
        >
            <ModalLayout
                buttons={[]}
                closeFit={true}
                className={styles.loginAndSignupModals}
            >
                <div className={styles.branding}>
                    <Link to="/">
                        <img src={logo} alt="Budget Butler" style={{cursor: 'pointer'}}/>
                    </Link>
                </div>
                <div className={styles.form}>
                    <h2>Log In</h2>
                    <p>Email</p>
                    <input
                        type="text"
                        value={username}
                        onChange={e => setUsername(e.target.value)}
                    />
                    <p>Password</p>
                    <input
                        type="password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        onKeyUp={e => {
                            if (e.key === "Enter") {
                                attemptLogin();
                            }
                        }}
                    />
                    { loginFailedError && <p className={styles.error}>Incorrect email or password</p> }
                    { error && <p className={styles.error}>An unknown error occurred</p> }
                    <button
                        onClick={attemptLogin}
                        className="primary"
                    >Log In</button>
                    <p>Don't have an account? <a href="#" onClick={() => navigate('/signup')}>Sign up</a></p>
                </div>
            </ModalLayout>
        </Modal>
    );
}

const SignUpModal = ({ show, onSignup }) => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [logo, setLogo] = useState();
    const navigate = useNavigate();

    useEffect(() => {
        const randomLogo = logos[Math.floor(Math.random() * logos.length)];
        setLogo(randomLogo);
    }, []);

    const attemptSignup = async () => {
        try {
            setError(null);
            await onSignup(username, password);
            // Navigate to app on successful signup
            navigate('/app/at-a-glance');
        } catch (e) {
            console.error(e);
            if (e instanceof SignupError) {
                setError(e.error);
            } else {
                setError({
                    non_field_errors: [ "An unknown error occurred" ]
                });
            }
        }
    };

    return (
        <Modal
            isOpen={show}
            style={mediumModal}
        >
            <ModalLayout
                buttons={[]}
                closeFit={true}
                className={styles.loginAndSignupModals}
            >
                <div className={styles.branding}>
                    <Link to="/">
                        <img src={logo} alt="Budget Butler" style={{cursor: 'pointer'}}/>
                    </Link>
                </div>
                <div className={styles.form}>
                    <h2>Sign Up</h2>
                    <p>Email</p>
                    <input
                        type="text"
                        value={username}
                        onChange={e => setUsername(e.target.value)}
                    />
                    { error?.username?.map(e => <p className={styles.error} key={e}>{e}</p>) }
                    <p>Password</p>
                    <input
                        type="password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        onKeyUp={e => {
                            if (e.key === "Enter") {
                                attemptSignup();
                            }
                        }}
                    />
                    { error?.password?.map(e => <p className={styles.error} key={e}>{e}</p>) }
                    { error?.non_field_errors?.map(e => <p className={styles.error} key={e}>{e}</p>) }
                    <button
                        onClick={attemptSignup}
                        className="primary"
                    >Sign Up</button>
                    <p>Already have an account? <a href="#" onClick={() => navigate('/login')}>Login</a></p>
                </div>
            </ModalLayout>        
        </Modal>
    );
};

const AccountMenu = ({ onAddAccountClicked, onLogout }) => {
    const { identity } = useAppContext();
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();

    return (
        <>
            <button
                className={styles.accountMenuButton}
                onClick={() => setIsOpen(true)}
                aria-label="Account menu"
            >
                <img src={tinyLogo} alt="Budget Butler logo"/>
            </button>
            <Modal
                isOpen={isOpen}
                onRequestClose={() => setIsOpen(false)}
                style={smallModal}
            >
                <ModalLayout
                    title="Account"
                    className={styles.accountModal}
                >
                    <p>You are logged in as {identity?.username}</p>
                    <button
                        onClick={() => {
                            onAddAccountClicked();
                            setIsOpen(false);
                        }}
                    >Add another bank account</button>
                    <button
                        onClick={() => {
                            onLogout();
                            setIsOpen(false);
                            navigate('/login', { replace: true });
                        }}
                    >Logout</button>
                </ModalLayout>
            </Modal>
        </>
    )
};

const Navigation = ({ 
    closeSideNavOnClick, 
    isSidenavOpen, 
    setIsSidenavOpen, 
    identity, 
    setShowAddAccountModal, 
    logout
}) => {
    const { currentSpace, spaces, setCurrentSpaceUri } = useAppContext();
    const handleClick = closeSideNavOnClick ? () => setIsSidenavOpen(false) : () => {};
    
    return (
        <nav className={styles.nav}>
            <ul role="list">
                <li className={styles.sidenavToggle}>
                    <button
                        onClick={() => setIsSidenavOpen(!isSidenavOpen)}
                    >
                        <span className="material-icons">menu</span>
                    </button>
                </li>
                <li className={styles.spaceSelector}>
                    <select 
                        value={currentSpace?.uri || ''} 
                        onChange={(e) => {
                            setCurrentSpaceUri(e.target.value);
                        }}
                    >
                        {spaces?.map(space => (
                            <option key={space.uri} value={space.uri}>
                                {space.name}
                            </option>
                        ))}
                    </select>
                </li>
                <li>
                    <Link to="/app/at-a-glance" onClick={handleClick}>
                        <span className="material-symbols-outlined">bar_chart_4_bars</span>
                        <span>At a glance</span>
                    </Link>
                </li>
                <li>
                    <Link to="/app/transactions" onClick={handleClick}>
                        <span className="material-symbols-outlined">wallet</span>
                        <span>Transactions</span>
                    </Link>
                </li>
                <li>
                    <Link to="/app/categories" onClick={handleClick}>
                        <span className="material-symbols-outlined">label</span>
                        <span>Categories</span>
                    </Link>
                </li>
                <li>
                    <Link to="/app/rules" onClick={handleClick}>
                        <span className="material-symbols-outlined">rule</span>
                        <span>Rules</span>
                    </Link>
                </li>
                <li className={styles.accountMenu}>
                    <AccountMenu
                        onAddAccountClicked={() => setShowAddAccountModal(true)}
                        onLogout={() => logout()}
                    />
                </li>
            </ul>
        </nav>
    );
};

const AccountsList = ({ accounts, providers, activeAccounts, setActiveAccounts }) => {
    if (!accounts || !providers) {
        return null;
    }
    
    const providersByUri = Object.fromEntries(
        providers.map(provider => ([provider.uri, provider]))
    );
    
    const accountsByProvider = accounts.reduce((result, account) => ({
        ...result,
        [account.provider]: [
            ...result[account.provider] || [],
            account
        ]
    }), {});
    
    return (
        <ul className={styles.accountsList}>
            {Object.entries(accountsByProvider)
                .map(([providerUri, accountsForProvider]) =>
                    <React.Fragment key={providerUri}>
                        <h3 className={styles.provider}>
                            <img src={providersByUri[providerUri]?.logoUrl}/>
                            <span>{providersByUri[providerUri]?.displayName}</span>
                        </h3>
                        {accountsForProvider
                            .toSorted((f, s) => f.displayName.localeCompare(s.displayName))
                            .map(account =>
                                <li
                                    key={account.uri}
                                    className={styles.account}
                                >
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={activeAccounts.includes(account.uri)}
                                            onChange={e => {
                                                let newActiveAccounts = activeAccounts.filter(
                                                    uri => uri !== account.uri
                                                );
                                                if(newActiveAccounts.length === 0) {
                                                    return;
                                                }
                                                if(e.target.checked) {
                                                    newActiveAccounts.push(account.uri);
                                                }
                                                setActiveAccounts(newActiveAccounts);
                                            }}
                                        />
                                        <span>
                                            {account.displayName} - {formatMoney(
                                                account.balance, account.currency
                                            )} ({account.id})
                                        </span>
                                    </label>
                                </li>
                            )}
                    </React.Fragment>
                )}
        </ul>
    );
};

const useUserQueries = userScopedFetch => {
    const queries = {
        identity: useQuery({
            queryKey: [ "identity", userScopedFetch?.ident ],
            queryFn: () => fetchSettings(userScopedFetch),
            enabled: !!userScopedFetch,
        }),
        providers: useQuery({
            queryKey: [ "providers", userScopedFetch?.ident ],
            queryFn: () => fetchProviders(userScopedFetch),
            enabled: !!userScopedFetch,
        }),
        spaces: useQuery({
            queryKey: [ "spaces", userScopedFetch?.ident ],
            queryFn: () => fetchSpaces(userScopedFetch),
            enabled: !!userScopedFetch,
        })
    };
    queries.isLoading = Object.values(queries).some(q => q.isLoading);
    queries.error = Object.values(queries).some(q => q.error);
    return queries;
};

const useSpaceScopedQueries = (spaceScopedFetch) => {
    const connectionsQuery = useQuery({
        queryKey: [ "connections", spaceScopedFetch?.ident ],
        queryFn: () => fetchConnections(spaceScopedFetch),
        enabled: !!spaceScopedFetch,
        refetchInterval: query => {
            const connections = query.state?.data || [];
            const syncing = connections?.some(c => c.dataState === "basic-resyncing" || c.dataState === "full-resyncing");
            return syncing ? 500 : false;
        }
    });

    const queries = {
        connections: connectionsQuery,
        accounts: useQuery({
            queryKey: [ "accounts", spaceScopedFetch?.ident, connectionsQuery.data ],
            queryFn: () => fetchAccounts(spaceScopedFetch),
            enabled: !!spaceScopedFetch
        }),
        categories: useQuery({
            queryKey: [ "categories", spaceScopedFetch?.ident, connectionsQuery.data ],
            queryFn: () => fetchCategories(spaceScopedFetch),
            enabled: !!spaceScopedFetch
        }),
    };
    queries.isLoading = Object.values(queries).some(q => q.isLoading);
    queries.error = Object.values(queries).some(q => q.error);
    return queries;
};

const ExpiredConnectionBanner = ({ connections, spaceScopedFetch }) => {
    const [loading, setLoading] = useState(false);
    const expiredConnections = connections?.filter(c => c.isExpired) || [];
    
    if (expiredConnections.length === 0) return null;
    
    const handleReconnect = async (connection) => {
        if (loading) return;
        
        setLoading(true);
        try {
            const reauthUrl = await fetchConnectionReauthUrl(connection.uri, spaceScopedFetch);
            window.location.href = reauthUrl;
        } catch (error) {
            console.error("Failed to get reauth URL:", error);
            setLoading(false);
        }
    };
    
    return (
        <div className={styles.expiredBanner}>
            <span className="material-icons">warning</span>
            <div className={styles.expiredContent}>
                <strong>Bank Connection Expired</strong>
                <p>
                    {expiredConnections.length === 1 
                        ? "Your bank connection has expired. " 
                        : `${expiredConnections.length} bank connections have expired. `}
                    Please reconnect to continue syncing your transactions.
                </p>
            </div>
            <div className={styles.expiredActions}>
                {expiredConnections.map(conn => (
                    <button 
                        key={conn.uri}
                        onClick={() => handleReconnect(conn)}
                        disabled={loading}
                        className={styles.reconnectButton}
                    >
                        {loading ? 'Connecting...' : 'Reconnect'}
                    </button>
                ))}
            </div>
        </div>
    );
};

const AppShell = ({
    userScopedFetch,
    logout
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    
    const userQueries = useUserQueries(userScopedFetch);
    const {
        identity: { data: identity, status: identityStatus },
        spaces: { data: spaces },
        providers: { data: providers }
    } = userQueries;

    const { currentSpace, setCurrentSpaceUri, spaceScopedFetch } = useSpaces(
        spaces, 
        userScopedFetch, 
        identity?.defaultSpace
    );

    const spaceScopedQueries = useSpaceScopedQueries(spaceScopedFetch);
    const {
        accounts: { data: accounts },
        categories: categoriesQuery,
        connections: connectionsQuery
    } = spaceScopedQueries;

    const connections = connectionsQuery.data;
    const isLoading = userQueries.isLoading || spaceScopedQueries.isLoading;
    const error = userQueries.error || spaceScopedQueries.error;

    const [bigNo, setBigNo] = useState(false);
    useEffect(() => {
        if (error) {
            setBigNo(true);
        }
    }, [error]);

    const [isSidenavOpen, setIsSidenavOpen] = useState(true);
    const [showAddAccountModal, setShowAddAccountModal] = useState(false);
    const [chatStatus, setChatStatus] = useState("closed");
    const [activeAccounts, setActiveAccounts] = useState([]);
    const [agentFilter, setAgentFilter] = useState(null);

    // Clear the agent filter when the user switches spaces
    useEffect(() => {
        setAgentFilter(null);
    }, [spaceScopedFetch]);

    useEffect(() => {
        if(!userScopedFetch) {
            navigate("/");
        }
    }, [userScopedFetch, navigate]);

    useEffect(() => {
        if(accounts) {
            setActiveAccounts(accounts.map(a => a.uri));
        }
    }, [accounts]);

    useEffect(() => {
        if(connections?.length === 0 && location.pathname !== "/app/at-a-glance") {
            navigate("/app/at-a-glance");
        }
    }, [connections, location, navigate]);

    useEffect(() => {
        if(connections?.length === 0 && !showAddAccountModal) {
            setShowAddAccountModal(true);
        }
    }, [connections]); // Once the modal is closed, it will not reopen unless connections change

    const syncing = connections?.some(c => c.dataState === "basic-resyncing");

    const resolveTransitions = event => {
        if(event.target !== event.currentTarget) return;
        if(chatStatus === "opening") {
            setChatStatus("open");
        }
        if(chatStatus === "closing") {
            setChatStatus("closed");
        }
    };

    if(identityStatus !== "success") {
        return null;
    }

    return (
        <AppProvider 
            userScopedFetch={userScopedFetch}
            spaceScopedFetch={spaceScopedFetch}
            accounts={accounts}
            identity={identity}
            activeAccounts={activeAccounts}
            categoriesQuery={categoriesQuery}
            connectionsQuery={connectionsQuery}
            setIsSidenavOpen={setIsSidenavOpen}
            setShowAddAccountModal={setShowAddAccountModal}
            spaces={spaces}
            currentSpace={currentSpace}
            setCurrentSpaceUri={setCurrentSpaceUri}
            agentFilter={agentFilter}
            setAgentFilter={setAgentFilter}
        >
            <div className={styles.appContainer}>
                <header className={styles.topNav}>
                    <Navigation 
                        closeSideNavOnClick={false}
                        isSidenavOpen={isSidenavOpen}
                        setIsSidenavOpen={setIsSidenavOpen}
                        identity={identity}
                        setShowAddAccountModal={setShowAddAccountModal}
                        logout={logout}
                    />
                </header>
                <ExpiredConnectionBanner 
                    connections={connections}
                    spaceScopedFetch={spaceScopedFetch}
                />
                <div className={cx(styles.contentContainer, {
                    [styles.sideNavCollapsed]: !isSidenavOpen,
                    [styles.chatPanelCollapsed]: chatStatus === "closed" || chatStatus === "closing"
                })}>
                    <aside
                        onTransitionEnd={resolveTransitions}
                        className={styles.sideNav}
                    >
                        <h2>
                            <span>Budget Butler</span>
                            <AccountMenu
                                onAddAccountClicked={() => setShowAddAccountModal(true)}
                                onLogout={() => logout()}
                            />
                        </h2>
                        <Navigation 
                            closeSideNavOnClick={true}
                            isSidenavOpen={isSidenavOpen}
                            setIsSidenavOpen={setIsSidenavOpen}
                            identity={identity}
                            setShowAddAccountModal={setShowAddAccountModal}
                            logout={logout}
                        />
                        {accounts?.length !== 0 &&
                            <nav className={styles.accountsNav}>
                                <AccountsList 
                                    accounts={accounts}
                                    providers={providers}
                                    activeAccounts={activeAccounts}
                                    setActiveAccounts={setActiveAccounts}
                                />
                            </nav>
                        }
                    </aside>
                    <main
                        onTransitionEnd={resolveTransitions}
                        className={styles.mainContent}
                    >
                        { !isLoading && <Outlet /> }
                    </main>
                    <aside
                        onTransitionEnd={resolveTransitions}
                        className={cx(styles.chatPanel)}
                    >
                        <LLMChat 
                            status={chatStatus}
                            onClose={() => setChatStatus("closing")}
                        />
                    </aside>
                    <button 
                        className={cx(styles.chatToggle, { [styles.chatToggleActive]: chatStatus === "closed" })}
                        onClick={() => setChatStatus("opening")}
                    >
                        <img src={tinyLogo} alt="Budget Butler" />
                    </button>
                </div>
                <Modal
                    isOpen={syncing}
                    style={smallModal}
                >
                    <ModalLayout
                        title="Loading..."
                        buttons={[]}
                        className={styles.loadingModal}
                    >
                        <p>We are loading your data. This may take a few seconds.</p>
                        <p>If this takes too long, please contact us.</p>
                        <div className={styles.loadingSpinner}>
                            <span className="material-icons">hourglass_empty</span>
                        </div>
                    </ModalLayout>
                </Modal>
                <ProviderSelectorModal
                    show={showAddAccountModal}
                    providers={providers ?? []}
                    onClose={() => setShowAddAccountModal(false)}
                    spaceScopedFetch={spaceScopedFetch}
                    className={styles.providerModalContent}
                />
                <Modal
                    isOpen={!!bigNo}
                    onRequestClose={() => setBigNo(false)}
                >
                    <h1>We are so sorry...</h1>
                    <p>There has been an error. Please contact us so we can fix this.</p>
                </Modal>
            </div>
        </AppProvider>
    );
};

const ConsumeCode = () => {
    const { userScopedFetch, connectionsQuery, setCurrentSpaceUri } = useAppContext();
    const navigate = useNavigate();
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code') || params.get('ref');

    const state = useMemo(() => {
        try {
            return JSON.parse(params.get('state') || 'null');
        } catch (e) {
            console.error('Invalid state parameter:', e);
            return null;
        }
    }, [params.get('state')]);

    useEffect(() => {
        if (code && userScopedFetch && state?.space) {
            const spaceScopedFetch = createSpaceScopedFetch(userScopedFetch, state.space);
            applyCode(code, state, spaceScopedFetch).then(() => {
                setCurrentSpaceUri(state.space);
                connectionsQuery.refetch();
                navigate('/app/at-a-glance');
            }).catch(e => {
                console.error("Failed to apply code:", e);
                navigate('/');
            });
        } else {
            navigate('/');
        }
    }, [code, userScopedFetch, state, navigate]);

    return null;
}

export default () => {
    const { login, logout, signup, userScopedFetch } = useAuth();

    return (
        <Router>
            <Routes>
                <Route path="/" element={
                    <HomePage
                        isAuthed={!!userScopedFetch}
                    />
                } />
                <Route path="/login" element={
                    <LoginModal 
                        show={true}
                        onLogin={login}
                    />
                } />
                <Route path="/signup" element={
                    <SignUpModal
                        show={true}
                        onSignup={signup}
                    />
                } />
                <Route path="" element={
                    <AppShell
                        userScopedFetch={userScopedFetch}
                        logout={logout}
                    />
                }>
                    <Route path="/code" element={<ConsumeCode/>} />
                    <Route path="/app/at-a-glance" element={<Dashboard />} />
                    <Route path="/app/transactions" element={<Transactions />} />
                    <Route path="/app/categories" element={<Categories />} />
                    <Route path="/app/rules" element={<Rules />} />
                </Route>
            </Routes>
        </Router>
    );
};
